import { ArrowBendUpLeft, PaperPlaneTilt, X } from '@phosphor-icons/react';
import type { Comment, PostMention, PublicUser } from '@sirocco/shared';
import { Capacitor } from '@capacitor/core';
import { useImperativeHandle, useRef, useState, type FormEvent, type RefObject } from 'react';
import { users as usersApi } from '../../api/endpoints';
import { Avatar } from '../../components/ui/Avatar';
import { IconButton } from '../../components/ui/Button';
import { useAsync } from '../../hooks/useAsync';
import { useSaisieExtensible } from '../../hooks/useSaisieExtensible';
import { insertMention, typingMention } from '../../utils/mentions';
import { t } from '../../i18n';

/**
 * Le champ pour repondre, avec ses mentions.
 *
 * Il vit a part parce que deux ecrans le posent : le fil d'un post, et
 * celui d'une reponse. Les y ecrire deux fois aurait fait deux
 * selecteurs de mentions a corriger, et l'un des deux aurait fini par
 * diverger de l'autre.
 */

/** Ce que l'ecran peut demander au composeur. */
export interface PoigneeComposeur {
  focus: () => void;
}

export function CommentComposer({
  poignee,
  repondA,
  onAnnulerLaReponse,
  onEnvoyer,
  envoi,
}: {
  poignee?: RefObject<PoigneeComposeur | null>;
  /** La reponse visee, quand on repond a quelqu'un plutot qu'au post. */
  repondA?: Comment | null;
  onAnnulerLaReponse?: () => void;
  onEnvoyer: (contenu: string, mentions: PostMention[]) => Promise<void>;
  envoi: boolean;
}) {
  const champ = useRef<HTMLTextAreaElement>(null);
  const formulaire = useRef<HTMLFormElement>(null);
  const [brouillon, setBrouillon] = useState('');
  /*
    On garde tous ceux qu'on a choisis, meme si leur pseudo est ensuite
    efface : le tri se fait a l'envoi, sur le texte definitif. Retirer au
    fil de la frappe ferait perdre une mention des qu'on corrige une
    lettre au milieu.
  */
  const [mentions, setMentions] = useState<PostMention[]>([]);
  const [tape, setTape] = useState<string | null>(null);

  useImperativeHandle(poignee, () => ({ focus: () => champ.current?.focus() }), []);

  /*
    Le champ grandit avec le commentaire, comme dans la messagerie, et la
    page monte avec lui. Il vit dans la barre du bas d'un ecran secondaire :
    c'est le corps de cet ecran qui defile au-dessus.
  */
  useSaisieExtensible(champ, brouillon, () =>
    formulaire.current?.closest('.sub-screen')?.querySelector<HTMLElement>('.sub-screen__body') ??
    null,
  );

  /*
    Les suggestions : au moins une lettre apres le « @ ».

    Sur le seul « @ », la liste entiere des membres n'aiderait personne et
    couterait une lecture complete a chaque frappe.
  */
  const { data: trouves } = useAsync<PublicUser[]>(
    () => (tape && tape.length >= 1 ? usersApi.search(tape) : Promise.resolve([])),
    [tape],
  );

  /** Le curseur bouge : on regarde si un pseudo est en cours de frappe. */
  function curseurDeplace() {
    const input = champ.current;
    if (!input) return;
    setTape(typingMention(input.value, input.selectionStart ?? input.value.length));
  }

  function choisir(membre: PublicUser) {
    const input = champ.current;
    if (!input) return;
    const suite = insertMention(
      brouillon,
      input.selectionStart ?? brouillon.length,
      membre.username,
    );
    setBrouillon(suite.content);
    setMentions((actuelles) =>
      actuelles.some((mention) => mention.id === membre.id)
        ? actuelles
        : [...actuelles, { id: membre.id, username: membre.username }],
    );
    setTape(null);

    /*
      Le curseur se repose apres l'insertion, une fois le rendu passe :
      React reecrit la valeur du champ et remettrait le curseur a la fin.
      Le champ garde le doigt, sinon le clavier se refermerait.
    */
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(suite.caret, suite.caret);
    });
  }

  async function envoyer(evenement: FormEvent) {
    evenement.preventDefault();
    const contenu = brouillon.trim();
    if (!contenu) return;
    await onEnvoyer(contenu, mentions);
    setBrouillon('');
    setMentions([]);
    setTape(null);
  }

  return (
    <form
      ref={formulaire}
      className="cm-composer"
      onSubmit={(evenement) => void envoyer(evenement)}
    >
      {/*
        La liste se pose au-dessus du champ, contrairement a l'ecran de
        redaction : le composeur est colle au bas de l'ecran, et une
        liste posee dessous sortirait sous le pli.
      */}
      {tape !== null && (trouves?.length ?? 0) > 0 && (
        <ul className="compose__mentions">
          {trouves!.slice(0, 5).map((membre) => (
            <li key={membre.id}>
              <button type="button" className="compose__mention" onClick={() => choisir(membre)}>
                <Avatar user={membre} size={30} />
                <span className="compose__mention-name">{membre.username}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/*
        A qui l'on repond, au-dessus du champ.

        Sans cette ligne, rien ne distinguait une reponse au post d'une
        reponse a quelqu'un : le curseur se posait dans le meme champ
        qu'avant, et l'on decouvrait apres coup ou le texte avait atterri.
      */}
      {repondA && (
        <p className="cm-composer__cible">
          <ArrowBendUpLeft className="sens-x" size={14} weight="bold" />
          {t.fil.composeur.reponseA} <strong>{repondA.author.username}</strong>
          {onAnnulerLaReponse && (
            <button
              type="button"
              className="cm-composer__annuler"
              aria-label={t.fil.composeur.annulerReponse}
              onClick={onAnnulerLaReponse}
            >
              <X size={13} weight="bold" />
            </button>
          )}
        </p>
      )}

      <div className="cm-composer__ligne">
        <textarea
          ref={champ}
          className="ms-composer__input"
          rows={1}
          /* La touche retour va a la ligne dans l'application ; Entree
             n'envoie qu'au clavier d'un ordinateur, comme la messagerie. */
          enterKeyHint="enter"
          onKeyDown={(evenement) => {
            if (evenement.key !== 'Enter' || evenement.shiftKey) return;
            if (evenement.nativeEvent.isComposing) return;
            if (Capacitor.isNativePlatform()) return;
            if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
            evenement.preventDefault();
            formulaire.current?.requestSubmit();
          }}
          placeholder={
            repondA ? t.fil.composeur.repondreA(repondA.author.username) : t.fil.composeur.ecrire
          }
          value={brouillon}
          maxLength={280}
          onChange={(evenement) => {
            setBrouillon(evenement.target.value);
            curseurDeplace();
          }}
          onKeyUp={curseurDeplace}
          onClick={curseurDeplace}
        />
        {/* Rond, comme dans les messages : le meme geste a la meme forme. */}
        <IconButton
          label={t.fil.composeur.envoyer}
          type="submit"
          variant="primary"
          size={42}
          className="ms-composer__send"
          disabled={envoi || brouillon.trim().length === 0}
        >
          <PaperPlaneTilt className="sens-x" size={19} weight="fill" />
        </IconButton>
      </div>
    </form>
  );
}
